import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import FreeGrantsTable from './FreeGrantsTable';
import RevokeConfirm from './RevokeConfirm';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import { readSavedFilters, usePersistFilters } from '../../hooks/filterPersistence';
import '../active-users/active-users.css';
import './free-grants.css';

const PAGE_SIZE = 20;
const FILTER_KEY = 'freeGrantsState';
const EMPTY_FILTERS = { status: 'active', billing: 'all', search: '', from: '', to: '' };

/**
 * Free Grants — every admin-granted free access ever made (feature 065). JWT-protected.
 *
 * This page is the audit surface the operator brief asked for: "which users have we provided the
 * free trials to". It is also where a failed store call is recovered from — a grant whose billing
 * call failed still gave the member their access, and Retry re-runs only that call.
 */
export default function FreeGrantsPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [grants, setGrants] = useState([]);
  const [counts, setCounts] = useState({ active: 0, expired: 0, revoked: 0 });
  const [summary, setSummary] = useState(null);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(() => readSavedFilters(FILTER_KEY) || EMPTY_FILTERS);
  const [notice, setNotice] = useState('');
  const [revoking, setRevoking] = useState(null);
  const [busyId, setBusyId] = useState(null);

  usePersistFilters(FILTER_KEY, filters);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(
    async (config) => {
      if (expire * 1000 < new Date().getTime()) {
        const res = await axios.get(`${baseUrl}/admin/api/token`);
        config.headers.Authorization = `Bearer ${res.data.accessToken}`;
        setToken(res.data.accessToken);
        setExpire(jwtDecode(res.data.accessToken).exp);
      }
      return config;
    },
    (err) => Promise.reject(err)
  );

  useEffect(() => { refreshToken(); }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) navigate('/');
    }
  };

  useEffect(() => {
    if (token) { loadList(page, filters); loadSummary(filters); }
    /* eslint-disable-next-line */
  }, [token]);

  const loadList = async (toPage, f) => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/free-access-grants`, {
        params: { ...f, page: toPage, limit: PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (res.data && res.data.data) || {};
      setGrants(data.grants || []);
      setCounts(data.counts || { active: 0, expired: 0, revoked: 0 });
      setTotalItem((data.pagination && data.pagination.total) || 0);
      setPage(toPage);
    } catch (e) {
      setError('Failed to load free access grants.');
      setGrants([]);
      setTotalItem(0);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async (f) => {
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/free-access-grants/summary`, {
        params: f,
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary((res.data && res.data.data) || null);
    } catch (e) {
      // Supporting detail only — its failure must not blank the list of grants.
      setSummary(null);
    }
  };

  const apply = (next) => {
    const f = { ...filters, ...next };
    setFilters(f);
    loadList(1, f);
    loadSummary(f);
  };

  const handleExport = async () => {
    setNotice('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/free-access-grants/export`, {
        params: filters,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'free-access-grants.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      if (res.headers && res.headers['x-export-truncated'] === 'true') {
        setNotice('Export capped at 50,000 rows — narrow the filters to get the rest.');
      }
    } catch (e) {
      setNotice('Export failed.');
    }
  };

  const handleRetry = async (grant) => {
    setBusyId(grant.id);
    setNotice('');
    try {
      const res = await axiosJWT.post(
        `${baseUrl}/admin/api/free-access-grants/${grant.id}/retry-billing`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = res.data?.data?.grant;
      setNotice(updated?.billing_message || 'Store request retried.');
      loadList(page, filters);
    } catch (e) {
      setNotice(e.response?.data?.message || 'Retry failed.');
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (reason) => {
    const grant = revoking;
    setBusyId(grant.id);
    try {
      const res = await axiosJWT.post(
        `${baseUrl}/admin/api/free-access-grants/${grant.id}/revoke`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // The billing note is the part an admin must actually read — surface it, do not swallow it.
      setNotice(res.data?.data?.billing_note || 'Grant revoked.');
      setRevoking(null);
      loadList(page, filters);
      loadSummary(filters);
    } catch (e) {
      setNotice(e.response?.data?.message || 'Revoke failed.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Header />
        <div className="au-page">
          <div className="au-head">
            <div>
              <h4>Free Grants</h4>
              <p>
                {counts.active} active · {counts.expired} expired · {counts.revoked} revoked
              </p>
            </div>
            <div className="au-controls">
              <input
                className="fg-input"
                style={{ maxWidth: 220 }}
                type="text"
                placeholder="Search name, email or reason"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') apply({ search: e.target.value }); }}
              />
              <select
                className="fg-input"
                style={{ maxWidth: 150 }}
                aria-label="Status"
                value={filters.status}
                onChange={(e) => apply({ status: e.target.value })}
              >
                <option value="active">Status: Active</option>
                <option value="expired">Status: Expired</option>
                <option value="revoked">Status: Revoked</option>
                <option value="all">Status: All</option>
              </select>
              <select
                className="fg-input"
                style={{ maxWidth: 190 }}
                aria-label="Billing"
                value={filters.billing}
                onChange={(e) => apply({ billing: e.target.value })}
              >
                <option value="all">Billing: All</option>
                <option value="success">Billing: Handled</option>
                <option value="partial">Billing: Partly covered</option>
                <option value="user_action_required">Billing: Waiting on member</option>
                <option value="failed">Billing: Failed</option>
                <option value="not_required">Billing: Not applicable</option>
              </select>
              <button type="button" className="fg-btn" onClick={handleExport}>Export CSV</button>
            </div>
          </div>

          {summary && (
            <div className="fg-summary">
              <div className="fg-summary-card">
                <span>Active grants</span><b>{summary.active}</b>
              </div>
              <div className="fg-summary-card">
                <span>Days granted</span><b>{summary.days_granted_total}</b>
              </div>
              <div className="fg-summary-card fg-summary-card--warn">
                <span>Waiting on member</span><b>{summary.billing_user_action}</b>
              </div>
              <div className="fg-summary-card fg-summary-card--crit">
                <span>Store call failed</span><b>{summary.billing_failed}</b>
              </div>
            </div>
          )}

          {notice && <div className="fg-alert fg-alert--ok" role="status">{notice}</div>}

          {loading && <TableSkeleton rows={6} />}
          {!loading && error && <ErrorState message={error} onRetry={() => loadList(page, filters)} />}
          {!loading && !error && grants.length === 0 && (
            <EmptyState message="No grants match these filters." />
          )}
          {!loading && !error && grants.length > 0 && (
            <>
              <FreeGrantsTable
                grants={grants}
                busyId={busyId}
                onRevoke={setRevoking}
                onRetry={handleRetry}
              />
              {totalItem > PAGE_SIZE && (
                <Pagination
                  activePage={page}
                  itemsCountPerPage={PAGE_SIZE}
                  totalItemsCount={totalItem}
                  pageRangeDisplayed={5}
                  onChange={(p) => loadList(p, filters)}
                  itemClass="page-item"
                  linkClass="page-link"
                />
              )}
            </>
          )}
        </div>
      </div>

      {revoking && (
        <RevokeConfirm
          grant={revoking}
          submitting={busyId === revoking.id}
          onCancel={() => setRevoking(null)}
          onConfirm={handleRevoke}
        />
      )}
    </>
  );
}
